import type {
  DestinationSectionComponent,
  FlattenPDPModels,
  PracticalInformationComponent,
  SitecoreFacility,
  SitecoreImagesWithTextResponse,
  SitecoreProductDetailPageFields,
  SitecoreRoomType,
  SitecoreUserRatingDetailedCategory,
  SitecoreVideoResponse,
  UserReviewsScores,
} from '@/cms/fields/types/product-detail-page'
import type { PriceTableAnalytics } from '@/scopes/prices/domain/types'
import type { IconGlyph } from '@sunwebgroup/ui-components'
import type { BreadcrumbItem, CMSPageFields, ProductMetadata } from '../types/common'
import { mapRenderingDatasourceId } from '@/cms/fields/mappers/common'
import getFlattenModels from '@/cms/fields/mappers/getFlattenModels'
import { parseSitecoreFloat } from '@/scopes/products/domain/utils'
import { IconGlyph as UdsIconGlyph } from '@sunwebgroup/ui-components/uds'
import { ascend, isEmpty, prop, slice, sortWith } from 'ramda'

export interface CMSProductDetailPageFields extends CMSPageFields {
  'userReviews.ratingAverage': number
  'userReviews.ratingsCount': number
  'userReviews.ratingByFacility': CMSUserReviewsFacilityRating<UdsIconGlyph>[]
  'userReviews.legacyRatingByFacility': CMSUserReviewsFacilityRating<IconGlyph>[]
  'userReviews.scoreByCategory': CMSUserReviewsCategoryScore[]
  'userReviews.mostRatedCategoryScore': CMSUserReviewsCategoryScore | null
  'userReviews.shouldShowRatings': boolean
  'PDP.url': string
  'PDP.title': string
  'PDP.media': CMSMedia[]
  'PDP.collectionName': string | null
  'PDP.collectionTheme': CMSCollectionTheme
  'PDP.roomTypes': CMSRoomType[]
  'PDP.roomTypes.additionalInfoHtml': string | null
  'PDP.rating': number
  'PDP.description': string
  'PDP.usps': string[]
  'PDP.attention': string
  'PDP.mealplans': CMSMealplans | null
  'PDP.surroundings': string[]
  'PDP.coordinates': { lat: number, lng: number }
  'PDP.facilities': CMSFacilityType[]
  'PDP.legacyFacilities': CMSFacilityType[]
  'Breadcrumb.elements': BreadcrumbItem[] | undefined
  'PDP.productMetadata': ProductMetadata | undefined
  'PDP.productId': number | null
  'PDP.id': string
  'PDP.PracticalInformation': PracticalInformationComponent | undefined
  'PDP.DestinationInformation': DestinationSectionComponent | undefined
  'PDP.pricetable.analytics': PriceTableAnalytics | null
  'PDP.pricetable.bookingStepsUrl': string
  'PDP.pricetable.priceAlertDatasourceId': string
  'PDP.bookingGateId': string
  'PDP.promotedPriceId': string
  'PDP.countryId': string
  'PDP.countryName': string
  'PDP.countryEnglishName': string
  'PDP.regionId': string
  'PDP.regionName': string
  'PDP.regionEnglishName': string
  'PDP.cityId': string
  'PDP.cityName': string
  'PDP.cityEnglishName': string
  'PDP.hasSustainabilityCertificate': boolean
}

export interface CMSRoomType {
  id: string
  name: string
  capacityText: string
  characteristics: string[]
  imageUrls: string[]
}

export interface CMSCollectionTheme {
  text: string
  background: string
}

export interface CMSUserReviewsFacilityRating<T> {
  icon: T
  score: number
  typeId: number
}

export interface CMSUserReviewsCategoryScore {
  score: number
  typeId: number
}

interface CMSMealplan {
  type: {
    code: string
    description: string
  }
}

export interface CMSMealplans {
  allInclusiveDescription: string | null
  description: string | null
  mealPlans: CMSMealplan[]
}

export enum CMSMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export interface CMSMedia {
  src: string
  alt: string
  type: CMSMediaType
  link?: string
}

export interface CMSFacilityType {
  id: number
  title: string
  icon: IconGlyph | UdsIconGlyph
  items: string[]
  order: number
  show: boolean
}

interface Geolocation {
  city: {
    id: string
    name: string
    englishName: string
  }
  country: {
    id: string
    name: string
    englishName: string
  }
  region: {
    id: string
    name: string
    englishName: string
  }
}

export function createProductDetailPageFieldsMapper() {
  function map(sitecoreFields: SitecoreProductDetailPageFields): CMSProductDetailPageFields {
    const {
      id,
      userratingsaverage,
      userratingscount,
      userratingsdetailedcategories,
      userreviewsscores,
      productname,
      imageswithtext,
      videos,
      collection,
      roomtypes,
      description,
      rating,
      attention,
      usps,
      mealplaninfo,
      locationinfo,
      'additional roomtypes info': additionalInfoHtml,
      latitude,
      longitude,
      facilities,
      hassustainabilitycertificate,
      models,
      deskid,
      _url,
      renderings,
    } = sitecoreFields

    const scoreByCategory = getScoreByCategory(userreviewsscores ?? [])
    const ratingsCount = userratingscount ? Number.parseInt(userratingscount, 10) : 0
    const flattenModels = getFlattenModels<FlattenPDPModels>(models ?? {}) as FlattenPDPModels
    const geolocation = getGeolocation(flattenModels)

    return {
      'userReviews.ratingAverage': parseSitecoreFloat(userratingsaverage),
      'userReviews.ratingsCount': ratingsCount,
      'userReviews.ratingByFacility': mapRatingByFacility<UdsIconGlyph>(userratingsdetailedcategories ?? []),
      'userReviews.legacyRatingByFacility': mapRatingByFacility<IconGlyph>(userratingsdetailedcategories ?? [], true),
      'userReviews.scoreByCategory': scoreByCategory,
      'userReviews.mostRatedCategoryScore': getMostRatedCategoryScore(scoreByCategory),
      'userReviews.shouldShowRatings': ratingsCount >= 3,
      'PDP.id': id ?? '',
      'PDP.url': _url,
      'PDP.title': productname ?? '',
      'PDP.media': mapImagesAndVideosToMedia(imageswithtext ?? [], videos ?? []),
      'PDP.collectionName': collection ?? null,
      'PDP.collectionTheme': mapCollectionTheme(collection),
      'PDP.roomTypes': mapRoomTypes(roomtypes ?? []),
      'PDP.roomTypes.additionalInfoHtml': additionalInfoHtml ?? null,
      'PDP.rating': parseSitecoreFloat(rating),
      'PDP.description': description ?? '',
      'PDP.usps': usps ?? [],
      'PDP.attention': attention ?? '',
      'PDP.mealplans': mealplaninfo
        ? {
            allInclusiveDescription: mealplaninfo.AllInclusiveDescription,
            description: mealplaninfo.Description,
            mealPlans:
              mealplaninfo.MealPlans?.map(({ Type }) => ({
                type: {
                  code: Type.Code,
                  description: Type.Description,
                },
              })) ?? [],
          }
        : { allInclusiveDescription: null, description: null, mealPlans: [] },
      'PDP.surroundings': locationinfo ?? [],
      'PDP.coordinates': {
        lat: parseSitecoreFloat(latitude),
        lng: parseSitecoreFloat(longitude),
      },
      'PDP.facilities': facilities ? mapFacilitiesByType<UdsIconGlyph>(facilities) : [],
      'PDP.legacyFacilities': facilities ? mapFacilitiesByType<IconGlyph>(facilities, true) : [],
      'Breadcrumb.elements': getBreadcrumbItems(flattenModels),
      'PDP.productId': deskid ?? 0,
      'PDP.PracticalInformation': getPracticalInformation(flattenModels),
      'PDP.DestinationInformation': getDestinationInformation(flattenModels),
      'PDP.pricetable.analytics': mapPriceTableAnalytics({ sitecoreFields }, geolocation),
      'PDP.pricetable.bookingStepsUrl': flattenModels.BookingGateModel?.BookingStepsUrl ?? '',
      'PDP.pricetable.priceAlertDatasourceId': flattenModels.BookingGateModel?.PriceAlertDatasourceId ?? '',
      'PDP.bookingGateId': mapRenderingDatasourceId(renderings, 'BookingGate'),
      'PDP.promotedPriceId': mapRenderingDatasourceId(renderings, 'PromotedPrice'),
      'PDP.productMetadata': getProductMetaData(flattenModels),
      'PDP.countryId': String(geolocation?.country.id ?? ''),
      'PDP.countryName': geolocation?.country.name ?? '',
      'PDP.countryEnglishName': geolocation?.country.englishName ?? '',
      'PDP.regionId': String(geolocation?.region.id ?? ''),
      'PDP.regionName': geolocation?.region.name ?? '',
      'PDP.regionEnglishName': geolocation?.region.englishName ?? '',
      'PDP.cityId': String(geolocation?.city.id ?? ''),
      'PDP.cityName': geolocation?.city.name ?? '',
      'PDP.cityEnglishName': geolocation?.city.englishName ?? '',
      'PDP.hasSustainabilityCertificate': Boolean(hassustainabilitycertificate),
    }
  }

  function mapRatingByFacility<T>(ratingCategories: SitecoreUserRatingDetailedCategory[], isLegacy = false) {
    const ratingCategoriesIconByTypeId: Record<number, IconGlyph | UdsIconGlyph> = isLegacy
      ? {
          10: 'IconPopularVillage',
          20: 'IconTag',
          30: 'IconPinMap',
          40: 'IconBed2Persons',
          50: 'IconSpray',
          60: 'IconRoomservice',
          70: 'IconKids',
          100: 'IconAmenities',
        }
      : {
          10: 'HouseWithHeart',
          20: 'Tag',
          30: 'MapPinOnMap',
          40: 'BedFor2Persons',
          50: 'Spray',
          60: 'ServingFoodWithCloche',
          70: 'Child',
          100: 'HouseBadgePlus',
        }

    return ratingCategories.map(({ Name: typeId, Rating: score }) => ({
      icon: ratingCategoriesIconByTypeId[typeId] as T,
      score,
      typeId,
    }))
  }

  function getScoreByCategory(reviewScores: UserReviewsScores[]) {
    const totalAmount = getScoresTotalAmount(reviewScores)
    return reviewScores.map((score) => {
      return {
        score: (score.Amount / totalAmount) * 100,
        typeId: score.Type,
      }
    })
  }

  function getScoresTotalAmount(reviewScores: UserReviewsScores[]) {
    return reviewScores.reduce((acc, score) => acc + score.Amount, 0) || 1
  }

  function getMostRatedCategoryScore(categoryScores: CMSUserReviewsCategoryScore[]) {
    if (isEmpty(categoryScores))
      return null

    return categoryScores?.reduce((maxItem, currentItem) =>
      currentItem.score > maxItem.score ? currentItem : maxItem,
    )
  }

  function mapImagesAndVideosToMedia(
    imagesWithText: SitecoreImagesWithTextResponse[],
    videos: SitecoreVideoResponse[],
  ) {
    const mappedImages
      = (imagesWithText
        ?.sort((a, b) => (a.Order < b.Order ? -1 : 1))
        .map(({ Image, Text }) => ({
          src: Image,
          alt: Text,
          type: CMSMediaType.IMAGE,
        })) as CMSMedia[]) ?? []

    const mappedVideos
      = (videos?.map(({ Link, Thumbnail, Title }) => ({
        src: Thumbnail,
        alt: Title,
        type: CMSMediaType.VIDEO,
        link: Link,
      })) as CMSMedia[]) ?? []

    const firstImages = slice(0, 2, mappedImages)
    const lastImages = slice(2, Infinity, mappedImages)

    return [...firstImages, ...mappedVideos, ...lastImages]
  }

  function mapCollectionTheme(collection?: string) {
    const defaultTheme = {
      background: '#FF333F',
      text: '#FFFFFF',
    }

    if (!collection)
      return defaultTheme

    const sitecoreToThemeMap: Record<string, CMSCollectionTheme> = {
      boutique: { background: '#1f448d', text: '#FFFFFF' },
      cycling: { background: '#333333', text: '#FFFFFF' },
      excellent: { background: '#5e2f7e', text: '#FFFFFF' },
      famfun: { background: '#ed2924', text: '#FFFFFF' },
      primavera: { background: '#00b49d', text: '#FFFFFF' },
      secrets: { background: '#00bfd4', text: '#FFFFFF' },
      selections: { background: '#f66e20', text: '#FFFFFF' },
      villages: { background: '#147a58', text: '#FFFFFF' },
      xperience: { background: '#ed2924', text: '#FFFFFF' },
      atlantis: { background: '#fe6400', text: '#FFFFFF' },
      totally: { background: '#ff6600', text: '#FFFFFF' },
    }

    return sitecoreToThemeMap?.[collection.toLowerCase()] ?? defaultTheme
  }

  function mapRoomTypes(roomTypes: SitecoreRoomType[]) {
    return roomTypes.map(({ Id, Name, Characteristics, Occupation, Images }) => ({
      id: Id,
      name: Name,
      capacityText: Occupation,
      characteristics: Characteristics,
      imageUrls: Images,
    }))
  }

  function mapFacilitiesByType<T>(sitecoreFacilities: SitecoreFacility[], isLegacy = false): CMSFacilityType[] {
    /**
     * TITLES AND ICONS MAPPING
     * (build the facility type array according to this order!)
     0 - Undefined
     10 - Optional -Optionele faciliteiten met bijkomende kosten
     20 - Deposit - Waarborgsom
     30 - Mandatory - Verplichte bijkomende kosten
     40 - GeneralFacility - Algemeen
     50 - Restaurant - Restaurants & bars
     60 - Pools - Zwemfaciliteiten
     70 - Wellness - Wellness
     80 - Sports - Sportfaciliteiten
     90 - Kids - Kinderfaciliteiten
     100 - Regular -
     110 - Exceptional - Corona maatregelen
     */

    const facilityTypeMapping: Record<number, { title: string, icon: IconGlyph | UdsIconGlyph, order: number }> = isLegacy
      ? {
          40: { title: 'practical_info_facilities_type_general', icon: 'IconInformationCircle', order: 1 },
          90: { title: 'practical_info_facilities_type_children', icon: 'IconChild', order: 2 },
          10: { title: 'practical_info_facilities_type_optional', icon: 'IconPlus', order: 3 },
          20: { title: 'practical_info_facilities_type_deposit', icon: 'IconMoney', order: 4 },
          50: { title: 'practical_info_facilities_type_restaurants', icon: 'IconCutlery', order: 5 },
          80: { title: 'practical_info_facilities_type_sports', icon: 'IconSportsRun', order: 6 },
          30: { title: 'practical_info_facilities_type_additional', icon: 'IconInvoice', order: 7 },
          70: { title: 'practical_info_facilities_type_wellness', icon: 'IconWellness', order: 8 },
          60: { title: 'practical_info_facilities_type_swimming', icon: 'IconSwimmingPool', order: 9 },
          100: { title: 'practical_info_facilities_type_regular', icon: 'IconQuestionCircle', order: 10 },
          110: { title: 'practical_info_facilities_type_exceptional', icon: 'IconWarningTriangle', order: 11 },
          0: { title: 'practical_info_facilities_type_undefined', icon: 'IconQuestionCircle', order: 12 },
        }
      : {
          40: { title: 'practical_info_facilities_type_general', icon: 'InformationInCircle', order: 1 },
          90: { title: 'practical_info_facilities_type_children', icon: 'Child', order: 2 },
          10: { title: 'practical_info_facilities_type_optional', icon: 'Plus', order: 3 },
          20: { title: 'practical_info_facilities_type_deposit', icon: 'PaymentCardWithCoins', order: 4 },
          50: { title: 'practical_info_facilities_type_restaurants', icon: 'ForkAndKnive', order: 5 },
          80: { title: 'practical_info_facilities_type_sports', icon: 'PersonStretching', order: 6 },
          30: { title: 'practical_info_facilities_type_additional', icon: 'DocumentWithEuro', order: 7 },
          70: { title: 'practical_info_facilities_type_wellness', icon: 'Lotus', order: 8 },
          60: { title: 'practical_info_facilities_type_swimming', icon: 'SwimmingPool', order: 9 },
          100: { title: 'practical_info_facilities_type_regular', icon: 'QuestionMarkInCircle', order: 10 },
          110: { title: 'practical_info_facilities_type_exceptional', icon: 'ExclamationInTriangle', order: 11 },
          0: { title: 'practical_info_facilities_type_undefined', icon: 'QuestionMarkInCircle', order: 12 },
        }

    const facilitiesShown: Record<number, boolean> = {
      0: true,
      10: true,
      20: true,
      30: true,
      40: true,
      50: true,
      60: true,
      70: true,
      80: true,
      90: true,
      100: false,
      110: true,
    }

    const mappedFacilities = sitecoreFacilities.map(({ FacilityType, Facilities }) => {
      const facilityInfo = facilityTypeMapping[FacilityType]
      return {
        id: FacilityType,
        title: facilityInfo?.title ?? 'Missing title',
        icon: facilityInfo?.icon ?? 'IconAmenities' as T,
        items: Facilities,
        // Place unknown facility types at the end of the list
        order: facilityInfo?.order ?? 1000,
        show: FacilityType in facilitiesShown ? facilitiesShown[FacilityType] : true,
      }
    })

    return sortWith([ascend(prop('order'))], mappedFacilities)
  }

  function getGeolocation({ BookingGateModel }: FlattenPDPModels): Geolocation {
    return {
      country: {
        id: BookingGateModel?.Country?.Id ?? '',
        name: BookingGateModel?.Country?.Name ?? '',
        englishName: BookingGateModel?.Country?.EnglishName ?? '',
      },
      region: {
        id: BookingGateModel?.Region?.Id ?? '',
        name: BookingGateModel?.Region?.Name ?? '',
        englishName: BookingGateModel?.Region?.EnglishName ?? '',
      },
      city: {
        id: BookingGateModel?.City?.Id ?? '',
        name: BookingGateModel?.City?.Name ?? '',
        englishName: BookingGateModel?.City?.EnglishName ?? '',
      },
    }
  }

  function mapPriceTableAnalytics(
    { sitecoreFields }: { sitecoreFields: SitecoreProductDetailPageFields },
    geolocation: Geolocation,
  ) {
    if (!sitecoreFields.models) {
      return null
    }

    const breadcrumbs = `${geolocation.country.name} > ${geolocation.region.name} > ${geolocation.city.name}`

    return {
      accoId: `${sitecoreFields.deskid ?? ''}`,
      accoName: sitecoreFields.productname ?? '',
      breadcrumbs,
      stars: sitecoreFields.rating ?? '',
      rating: String(parseSitecoreFloat(sitecoreFields.userratingsaverage)),
      cityId: String(geolocation.city.id),
      regionId: String(geolocation.region.id),
      countryId: String(geolocation.country.id),
      cityEnglishName: String(geolocation.city.englishName),
      regionEnglishName: String(geolocation.region.englishName),
      countryEnglishName: String(geolocation.country.englishName),
      hasSustainabilityCertificate: sitecoreFields.hassustainabilitycertificate === 1,
    }
  }

  return { map }
}

function getBreadcrumbItems({ BreadcrumbModel }: FlattenPDPModels): BreadcrumbItem[] {
  const breadcrumbItems = BreadcrumbModel?.BreadcrumbElements

  if (!breadcrumbItems) {
    return []
  }

  return breadcrumbItems.map(({ Name, Url }) => ({
    text: Name,
    href: Url,
  }))
}

function getPracticalInformation({ PracticalInfoModel }: FlattenPDPModels): PracticalInformationComponent {
  const practicalInfo = PracticalInfoModel

  const flightInformation
    = practicalInfo?.FlightInformationTitle || practicalInfo?.FlightInformation
      ? {
          title: practicalInfo?.FlightInformationTitle ?? null,
          details: practicalInfo?.FlightInformation ?? null,
        }
      : null
  const countryInformation
    = practicalInfo?.CountryPracticalInfoTitle || practicalInfo?.CountryPracticalInformation
      ? {
          title: practicalInfo?.CountryPracticalInfoTitle ?? null,
          details: practicalInfo?.CountryPracticalInformation ?? null,
        }
      : null

  return {
    headerTitle: practicalInfo?.MainTitle ?? null,
    goodToKnowTitle: practicalInfo?.GoodToKnowTitle ?? null,
    mealplansTitle: practicalInfo?.MealPlansTitle ?? null,
    flightInformation,
    surroundingsTitle: practicalInfo?.SurroundingsTitle ?? null,
    countryInformation,
  }
}

function getDestinationInformation({ DestinationSectionModel }: FlattenPDPModels): DestinationSectionComponent {
  const destinationInfo = DestinationSectionModel

  return {
    sectionTitle: destinationInfo?.MainTitle ?? '',
    imageUrl: destinationInfo?.ImageUri ?? '',
    regionName: destinationInfo?.RegionName ?? '',
    destinationHref: destinationInfo?.Url ?? '',
  }
}

function getProductMetaData({ ProductMetaDataModel }: FlattenPDPModels): ProductMetadata {
  return {
    metaTitle: ProductMetaDataModel?.MetaTitle ?? '',
    metaDescription: ProductMetaDataModel?.MetaDescription ?? '',
  }
}
